// src/pages/OrganizationsPage.js
import React, { useEffect, useState, useContext } from 'react';
import apiClient from '../api/apiClient.js';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Snackbar,
  InputAdornment,
  Chip,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LogoutIcon from '@mui/icons-material/Logout';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import { useNavigate } from 'react-router-dom';
import { OrgContext } from '../contexts/OrgContext';
import { AuthContext } from '../contexts/AuthContext';
import { updateOrganizationName, deleteOrganization } from '../services/organizationService';

function OrganizationsPage() {
  const { isAuthenticated, logout } = useContext(AuthContext);
  const [orgs, setOrgs] = useState([]);
  const [filteredOrgs, setFilteredOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // State for Dialog
  const [open, setOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const [snackbarErrorOpen, setSnackbarErrorOpen] = useState(false);
  const [snackbarCreateErrorOpen, setSnackbarCreateErrorOpen] = useState(false);
  const [snackbarSuccessOpen, setSnackbarSuccessOpen] = useState(false);

  const navigate = useNavigate();
  const { setOrgId, setOrgName } = useContext(OrgContext);

  const [editingOrgId, setEditingOrgId] = useState(null);
  const [editingOrgName, setEditingOrgName] = useState('');
  const [editing, setEditing] = useState(false);

  // State for delete functionality
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Add authentication check
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Fetch organizations on component mount
  useEffect(() => {
    fetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter organizations based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOrgs(orgs);
    } else {
      const filtered = orgs.filter(org =>
        org.org_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.org_id.toString().includes(searchTerm)
      );
      setFilteredOrgs(filtered);
    }
  }, [orgs, searchTerm]);

  const fetchOrganizations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/organizations');
      setOrgs(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching organizations.');
      setSnackbarErrorOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Open Dialog
  const handleOpen = () => {
    setNewOrgName('');
    setCreateError(null);
    setOpen(true);
  };

  // Close Dialog
  const handleClose = () => {
    if (!creating) {
      setOpen(false);
    }
  };

  // Handle Organization Creation
  const handleCreateOrg = async () => {
    if (!newOrgName.trim()) {
      setCreateError('Organization name cannot be empty.');
      setSnackbarCreateErrorOpen(true);
      return;
    }

    setCreating(true);
    setCreateError(null);
    try {
      await apiClient.post('/organizations/create', { name: newOrgName.trim() });
      setOpen(false);
      fetchOrganizations(); // Refresh the org list
      setSnackbarSuccessOpen(true);
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Error creating organization.');
      setSnackbarCreateErrorOpen(true);
    } finally {
      setCreating(false);
    }
  };

  const handleOrgClick = (orgId, orgName) => {
    setOrgId(orgId); // Set orgId in context
    setOrgName(orgName); // Set orgName in context
    navigate(`/${orgId}/`, { replace: true }); // Use replace to prevent back navigation issues
  };

  const handleViewReports = () => {
    navigate('/reports', { replace: false });
  };

  const handleEditOpen = (orgId, orgName) => {
    setEditingOrgId(orgId);
    setEditingOrgName(orgName);
    setEditing(true);
  };

  const handleEditClose = () => {
    setEditing(false);
    setEditingOrgId(null);
    setEditingOrgName('');
  };

  const handleEditOrg = async () => {
    if (!editingOrgName.trim()) {
      setError('Organization name cannot be empty.');
      setSnackbarErrorOpen(true);
      return;
    }

    try {
      await updateOrganizationName(editingOrgId, editingOrgName.trim());
      handleEditClose();
      fetchOrganizations(); // Refresh the org list
      setSnackbarSuccessOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating organization.');
      setSnackbarErrorOpen(true);
    }
  };

  // Delete handlers - update to remove menu dependency
  const handleDeleteOpen = (org) => {
    setOrgToDelete(org);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteConfirmOpen(false);
    setOrgToDelete(null);
  };

  const handleDeleteOrg = async () => {
    if (!orgToDelete) return;

    setDeleting(true);
    try {
      await deleteOrganization(orgToDelete.org_id);
      handleDeleteClose();
      fetchOrganizations(); // Refresh the org list
      setSnackbarSuccessOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting organization.');
      setSnackbarErrorOpen(true);
    } finally {
      setDeleting(false);
    }
  };

  // Logout handler
  const handleLogout = () => {
    logout();
    localStorage.clear(); // Clear all localStorage
    navigate('/auth');
  };

  return (
    <Box sx={{ p: 4, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
          Organizations
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleViewReports}
            size="medium"
            sx={{ 
              fontWeight: 600,
              borderWidth: 2,
              '&:hover': { borderWidth: 2 }
            }}
          >
            Reports
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpen}
            size="medium"
            sx={{ 
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': { boxShadow: 4 }
            }}
          >
            Create Organization
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            size="medium"
            sx={{ 
              fontWeight: 600,
              borderWidth: 2,
              color: '#ff6b35',
              borderColor: '#ff6b35',
              '&:hover': { 
                borderWidth: 2,
                borderColor: '#ff5722',
                backgroundColor: alpha('#ff6b35', 0.1)
              }
            }}
          >
            Logout
          </Button>
        </Box>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search organizations by name or ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#ffffff' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 600,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: '#2e2e2e',
              color: '#ffffff',
              border: '1px solid #555',
              '&:hover': {
                backgroundColor: '#3a3a3a',
                borderColor: '#777',
              },
              '&.Mui-focused': {
                backgroundColor: '#2e2e2e',
                borderColor: '#1976d2',
              },
              '& fieldset': {
                borderColor: '#555',
              },
              '&:hover fieldset': {
                borderColor: '#777',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1976d2',
              },
            },
            '& .MuiInputBase-input': {
              color: '#ffffff',
              '&::placeholder': {
                color: '#aaa',
                opacity: 1,
              },
            },
          }}
        />
        {searchTerm && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Found {filteredOrgs.length} organization{filteredOrgs.length !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress size={40} thickness={4} />
        </Box>
      )}

      {error && (
        <Snackbar
          open={snackbarErrorOpen}
          onClose={() => setSnackbarErrorOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          message={error}
          ContentProps={{
            sx: {
              color: '#ffffff',
              backgroundColor: 'error.main',
              textAlign: 'center'
            }
          }}
        />
      )}

      {/* Organizations List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {filteredOrgs.map((org) => (
          <Card 
            key={org.org_id}
            sx={{ 
              transition: 'all 0.2s ease-in-out',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4,
                borderColor: 'primary.main',
              },
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                }}
              >
                {/* Organization Info */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flex: 1,
                    cursor: 'pointer',
                    minWidth: 0,
                  }}
                  onClick={() => handleOrgClick(org.org_id, org.org_name)}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      flexShrink: 0,
                    }}
                  >
                    <BusinessIcon fontSize="medium" />
                  </Box>
                  
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        color: '#ffffff',
                        mb: 0.5,
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {org.org_name}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Chip
                        label={`ID: ${org.org_id}`}
                        size="small"
                        variant="outlined"
                        sx={{ 
                          fontWeight: 500,
                          color: '#ffffff',
                          borderColor: '#ffffff',
                          '&:hover': {
                            backgroundColor: alpha('#ffffff', 0.1),
                          }
                        }}
                      />
                      <Chip
                        label={org.role}
                        size="small"
                        color="secondary"
                        sx={{ fontWeight: 500 }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1,
                  flexShrink: 0,
                  flexDirection: { xs: 'row', sm: 'row' },
                  width: { xs: '100%', sm: 'auto' },
                }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => handleOrgClick(org.org_id, org.org_name)}
                    size="small"
                    sx={{
                      px: 2,
                      py: 0.75,
                      fontWeight: 600,
                      flex: { xs: 1, sm: 'none' },
                      minWidth: { xs: 'auto', sm: 100 },
                    }}
                  >
                    Open
                  </Button>
                  
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditOpen(org.org_id, org.org_name)}
                    size="small"
                    sx={{
                      px: 2,
                      py: 0.75,
                      fontWeight: 600,
                      borderWidth: 1.5,
                      flex: { xs: 1, sm: 'none' },
                      minWidth: { xs: 'auto', sm: 80 },
                      '&:hover': { borderWidth: 1.5 }
                    }}
                  >
                    Edit
                  </Button>
                  
                  <Button
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteOpen(org)}
                    size="small"
                    sx={{
                      px: 2,
                      py: 0.75,
                      fontWeight: 600,
                      borderWidth: 1.5,
                      color: '#ff6b35',
                      borderColor: '#ff6b35',
                      flex: { xs: 1, sm: 'none' },
                      minWidth: { xs: 'auto', sm: 90 },
                      '&:hover': { 
                        borderWidth: 1.5,
                        borderColor: '#ff5722',
                        backgroundColor: alpha('#ff6b35', 0.1)
                      }
                    }}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {!loading && filteredOrgs.length === 0 && !searchTerm && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No organizations found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create your first organization to get started
          </Typography>
        </Box>
      )}

      {!loading && filteredOrgs.length === 0 && searchTerm && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No organizations match your search
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try a different search term or create a new organization
          </Typography>
        </Box>
      )}

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Create New Organization</DialogTitle>
        <DialogContent>
          {createError && (
            <Snackbar
              open={snackbarCreateErrorOpen}
              onClose={() => setSnackbarCreateErrorOpen(false)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              message={createError}
              ContentProps={{
                sx: {
                  color: '#ffffff',
                  backgroundColor: 'error.main',
                  textAlign: 'center'
                }
              }}
            />
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Organization Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)}
            disabled={creating}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateOrg}
            variant="contained"
            color="primary"
            disabled={creating}
          >
            {creating ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editing} onClose={handleEditClose} fullWidth maxWidth="sm">
        <DialogTitle>Edit Organization Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Organization Name"
            type="text"
            fullWidth
            variant="outlined"
            value={editingOrgName}
            onChange={(e) => setEditingOrgName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button
            onClick={handleEditOrg}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Delete Organization</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{orgToDelete?.org_name}"? 
            This action will permanently delete the organization and all its related data including:
          </Typography>
          <Box component="ul" sx={{ mt: 2, pl: 2 }}>
            <li>All products</li>
            <li>All inventory items</li>
            <li>All customers</li>
            <li>All suppliers</li>
            <li>All orders</li>
            <li>All related data</li>
          </Box>
          <Typography color="error" sx={{ mt: 2, fontWeight: 'bold' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteOrg}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete Organization'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarSuccessOpen}
        onClose={() => setSnackbarSuccessOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message="Operation completed successfully!"
        ContentProps={{
          sx: {
            color: '#ffffff',
            backgroundColor: 'primary.main',
            textAlign: 'center'
          }
        }}
      />
    </Box>
  );
}

export default OrganizationsPage;
